import { motion } from 'framer-motion';
import { Lock, BookOpen, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/i18n/LanguageContext';

export function TreslogAccessGate() {
  const navigate = useNavigate();
  const { language } = useLanguage();

  const content = {
    en: {
      title: 'Your Private Lifetime Reflection Space',
      subtitle: 'Available with the Legacy plan',
      description: 'TRESLOG is your private lifetime reflection space. Write about your life in the third person—gaining clarity and compassion. Your entries are yours forever, never metered or limited.',
      feature1: 'Private lifetime self-blog—yours forever',
      feature2: 'Never consumes announcement credits',
      feature3: 'You control what stays private or shared',
      cta: 'Unlock with Legacy',
      learnMore: 'Learn more about Legacy',
      badge: 'Private • Lifetime • User-Controlled',
    },
    es: {
      title: 'Tu Espacio Privado de Reflexión de por Vida',
      subtitle: 'Disponible con el plan Legacy',
      description: 'TRESLOG es tu espacio privado de reflexión de por vida. Escribe sobre tu vida en tercera persona—ganando claridad y compasión. Tus entradas son tuyas para siempre, nunca medidas ni limitadas.',
      feature1: 'Blog personal privado de por vida—tuyo para siempre',
      feature2: 'Nunca consume créditos de anuncios',
      feature3: 'Tú controlas qué es privado o compartido',
      cta: 'Desbloquear con Legacy',
      learnMore: 'Más información sobre Legacy',
      badge: 'Privado • De por Vida • Controlado por Ti',
    },
  };

  const t = content[language as keyof typeof content] || content.en;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="flex flex-col items-center justify-center py-16 px-4"
    >
      <Card className="max-w-lg w-full">
        <CardContent className="pt-8 pb-6 text-center">
          <div className="relative mb-6 mx-auto w-fit">
            <div className="absolute inset-0 bg-primary/10 rounded-full blur-xl scale-150" />
            <div className="relative bg-gradient-to-br from-primary/20 to-primary/5 p-6 rounded-full">
              <Lock className="h-12 w-12 text-primary" />
            </div>
          </div>

          <h2 className="text-xl font-bold text-foreground mb-2">
            {t.title}
          </h2>
          
          <p className="text-xs font-medium text-primary mb-3">
            {t.badge}
          </p>

          <p className="text-muted-foreground mb-6 leading-relaxed">
            {t.description}
          </p>

          <div className="space-y-2 mb-8 text-left">
            {[t.feature1, t.feature2, t.feature3].map((feature, idx) => (
              <div key={idx} className="flex items-center gap-3 text-sm">
                <Sparkles className="h-4 w-4 text-primary flex-shrink-0" />
                <span className="text-foreground">{feature}</span>
              </div>
            ))}
          </div>

          <div className="space-y-3">
            <Button
              size="lg"
              className="w-full gap-2"
              onClick={() => navigate('/#pricing')}
            >
              <BookOpen className="h-5 w-5" />
              {t.cta}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/#treslog')}
            >
              {t.learnMore}
            </Button>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
